import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { MyProfileDTO } from '@/api/adapters/my-profile';

import { PasswordChangeForm } from './PasswordChangeForm';

const PASSWORD_PANEL_ID = 'password-change-panel';

const formatIsoToDot = (iso: string | null): string | null => {
  if (!iso) return null;
  return iso.slice(0, 10).replace(/-/g, '.');
};

interface PasswordCardProps {
  profile: MyProfileDTO;
}

export function PasswordCard({ profile }: PasswordCardProps) {
  const { t } = useTranslation('myProfile');
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const formatted = formatIsoToDot(profile.passwordChangedAt);
  const subtitle = formatted
    ? t('cards.password.lastChanged', { date: formatted })
    : t('cards.password.lastChangedNever');

  const handleToggle = useCallback(() => {
    // Cancel clears the form by unmounting it (matches prototype's full reset).
    setIsOpen((prev) => !prev);
  }, []);

  const handleSuccess = useCallback(() => {
    setIsOpen(false);
    // a11y: return focus to the change-toggle after auto-collapse.
    window.setTimeout(() => toggleRef.current?.focus(), 0);
  }, []);

  return (
    <div className="card profile-card">
      <div className="card__header profile-card__header">
        <div>
          <h3 className="card__title">{t('cards.password.title')}</h3>
          <div className="card__subtitle">{subtitle}</div>
        </div>
        <button
          ref={toggleRef}
          type="button"
          className="btn btn--secondary btn--sm"
          aria-expanded={isOpen}
          aria-controls={PASSWORD_PANEL_ID}
          onClick={handleToggle}
        >
          {isOpen ? t('actions.cancel') : t('actions.change')}
        </button>
      </div>
      {isOpen ? (
        <div id={PASSWORD_PANEL_ID}>
          <PasswordChangeForm onSuccess={handleSuccess} />
        </div>
      ) : null}
    </div>
  );
}
